import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import Logo from '@/components/scorpion/Logo';

const NAV = [
  { label: 'Capital', href: '#services' },
  { label: 'Manifesto', href: '#manifesto' },
  { label: 'Process', href: '#process' },
  { label: 'Voices', href: '#voices' },
  { label: 'FAQ', href: '#faq' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,border,padding] duration-500 ${
        scrolled
          ? 'bg-black/70 backdrop-blur-xl border-b border-white/5 py-1.5'
          : 'bg-transparent py-2'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex items-center justify-between">
        <a
          href="#top"
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <Logo height={scrolled ? 36 : 44} variant="icon" className="transition-[height] duration-500" />
          <div className="leading-none hidden sm:block">
            <div className="font-serif text-[#f5f5f0] text-base lg:text-lg tracking-[0.02em]">
              Scorpion<span className="text-[#c2a978] italic"> Funding</span>
            </div>
            <div className="text-[9px] tracking-[0.4em] uppercase text-[#c2a978] mt-1.5">
              Capital for the Relentless
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="text-[10px] tracking-[0.28em] uppercase text-[#f5f5f0]/70 hover:text-[#c2a978] transition-colors duration-300"
              data-testid={`nav-link-${n.label.toLowerCase()}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://app.scorpionfunding.com" target="_blank" rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 border border-[#c2a978] text-[#c2a978] hover:bg-[#c2a978] hover:text-black transition-colors duration-300 text-[10px] tracking-[0.28em] uppercase font-medium"
            data-testid="nav-cta"
          >
            Apply Now
          </a>
          <button
            className="lg:hidden text-[#f5f5f0] p-1.5"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#050505] border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="text-sm tracking-[0.28em] uppercase text-[#f5f5f0]/80 py-2"
                >
                  {n.label}
                </a>
              ))}
              <a
                href="https://app.scorpionfunding.com" target="_blank" rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="btn-gold mt-3 justify-center"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
